import { Box, Grid, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import TextInput from "./forms/TextInput/TextInput";
import PhoneInput from "./forms/PhoneInput/PhoneInput";
import { isValidPhone } from "utils/validations";

type ContactInformationFormValues = {
  homePhone: string;
  cellPhone: string;
  email: string;
};

type ContactInformationFormProps = {
  homePhone?: string;
  cellPhone?: string;
  email?: string;
  memberId?: string;
  readOnly?: boolean;
  initialLoading?: boolean;
  onUpdateSuccess?: () => void;
};

const ContactInformationForm: React.FC<ContactInformationFormProps> = (
  props
) => {
  const { control, handleSubmit, reset } =
    useForm<ContactInformationFormValues>({
      defaultValues: {
        homePhone: props.homePhone ?? "",
        cellPhone: props.cellPhone ?? "",
        email: props.email ?? "",
      },
    });

  useEffect(() => {
    if (props.homePhone || props.cellPhone || props.email) {
      reset({
        homePhone: props.homePhone ?? "",
        cellPhone: props.cellPhone ?? "",
        email: props.email ?? "",
      });
    }
  }, [props.homePhone, props.cellPhone, props.email]);

  const initialLoading = props.initialLoading;

  const disabled = props.readOnly;

  return (
    <Box mb={4}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 1,
          mb: 2,
        }}
      >
        <Typography variant="h4">Contact Information</Typography>
      </Box>
      <Grid
        container
        spacing={2}
        columns={{
          xs: 1,
          sm: 3,
          md: 5,
        }}
      >
        <Grid size={1}>
          <Controller
            name="homePhone"
            control={control}
            rules={{
              required: "Phone number is required",
              validate: (val) =>
                isValidPhone(val ?? "") ||
                "Please provide a valid US phone number",
            }}
            render={({ field, fieldState: { error } }) => (
              <PhoneInput
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={field.onBlur}
                ref={field.ref}
                label="Home Phone Number"
                fullWidth
                disabled={disabled}
                initialLoading={initialLoading}
                error={!!error?.message}
                helperText={error?.message || ""}
              />
            )}
          />
        </Grid>
        <Grid size={1}>
          <Controller
            name="cellPhone"
            control={control}
            rules={{
              validate: (val) =>
                !val ||
                isValidPhone(val) ||
                "Please provide a valid US phone number",
            }}
            render={({ field, fieldState: { error } }) => (
              <PhoneInput
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={field.onBlur}
                ref={field.ref}
                label="Mobile Phone Number"
                fullWidth
                disabled={disabled}
                initialLoading={initialLoading}
                error={!!error?.message}
                helperText={error?.message || ""}
              />
            )}
          />
        </Grid>
        <Grid size={1}>
          <Controller
            name="email"
            control={control}
            rules={{
              required: "Email address is required",
            }}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                label="Email Address"
                fullWidth
                disabled={disabled}
                error={!!error?.message}
                helperText={error?.message || ""}
                initialLoading={initialLoading}
              />
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContactInformationForm;
